import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useRegisterStaffMutation } from "../../services/user/userApi";
import { Check, Eye, EyeOff } from "lucide-react";

const CreateUserModal = ({ isOpen, onClose }) => {
  const [apiError, setApiError] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("No file chosen");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerStaff, { isLoading }] = useRegisterStaffMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      profilePicture: null,
    },
  });

  const profilePictureField = register("profilePicture", {
    required: "Profile picture is required",
  });

  const handleClose = () => {
    reset();
    setApiError("");
    setSelectedFileName("No file chosen");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
    onClose();
  };

  const handleSuccessClose = () => {
    setIsSuccessModalOpen(false);
  };

  useEffect(() => {
    if (!isOpen && !isSuccessModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isSuccessModalOpen]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const onSubmit = async (formValues) => {
    try {
      setApiError("");
      const payload = new FormData();
      payload.append("firstName", formValues.firstName.trim());
      payload.append("lastName", formValues.lastName.trim());
      payload.append("email", formValues.email.trim());
      payload.append("password", formValues.password);

      if (formValues.profilePicture?.[0]) {
        payload.append("profilePicture", formValues.profilePicture[0]);
      }
     
     
      await registerStaff(payload).unwrap();

      handleClose();
      setIsSuccessModalOpen(true);
    } catch (error) {
      setApiError(error?.data?.message || "Unable to create user. Please try again.");
    }
  };

  if (!isOpen && !isSuccessModalOpen) {
    return null;
  }

  return (
    <>
      {isOpen ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden bg-black/45 p-6 sm:p-10"
          onClick={handleClose}
        >
          <div
            className="max-h-[min(94vh,calc(100dvh-1.5rem))] w-full max-w-[933px] overflow-y-auto rounded-2xl border border-[#00000014] bg-[#EAE9E9] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-4 pb-5 pt-6 sm:px-6 sm:pb-7 sm:pt-8 lg:px-12">
              <h3 className="text-lg font-medium leading-6 text-black sm:text-xl">
                Create a User
              </h3>

              <form className="" noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className="my-5 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-[3px] border-[#E5D7EB] bg-[#F1EAF5] text-[#C8C0CF] sm:h-24 sm:w-24">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Selected profile"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <svg
                      width="42"
                      height="42"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                        fill="currentColor"
                      />
                      <path
                        d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="mb-2 text-base font-semibold text-[#000] sm:text-lg">
                    Profile Picture
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <label className="w-fit cursor-pointer rounded-full bg-[#E8DCEA] px-5 py-2 text-base font-semibold text-[#923D8C] sm:px-6 sm:text-lg">
                      Choose file
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        name={profilePictureField.name}
                        ref={profilePictureField.ref}
                        onBlur={profilePictureField.onBlur}
                        onChange={(event) => {
                          profilePictureField.onChange(event);
                          const file = event.target.files?.[0];
                          setSelectedFileName(file ? file.name : "No file chosen");
                          setPreviewUrl((current) => {
                            if (current) {
                              URL.revokeObjectURL(current);
                            }
                            return file ? URL.createObjectURL(file) : null;
                          });
                        }}
                      />
                    </label>
                    <span className="max-w-full truncate text-base text-[#667085] sm:max-w-[300px] sm:text-2xl">
                      {selectedFileName}
                    </span>
                  </div>
                  {errors.profilePicture?.message ? (
                    <p className="mt-2 text-sm text-[#DC2626]">
                      {errors.profilePicture.message}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:gap-8 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block  text-black">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="First name"
                    className="h-11 w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-base text-black outline-none placeholder:text-[#98A2B3] sm:text-xl"
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                  />
                  {errors.firstName?.message ? (
                    <p className="mt-1.5 text-sm text-[#DC2626]">
                      {errors.firstName.message}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1.5 block  text-black">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Last name"
                    className="h-11 w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-base text-black outline-none placeholder:text-[#98A2B3] sm:text-xl"
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                  />
                  {errors.lastName?.message ? (
                    <p className="mt-1.5 text-sm text-[#DC2626]">
                      {errors.lastName.message}
                    </p>
                  ) : null}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block  text-black">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email"
                    className="h-11 w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 text-base text-black outline-none placeholder:text-[#98A2B3] sm:text-xl"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                  />
                  {errors.email?.message ? (
                    <p className="mt-1.5 text-sm text-[#DC2626]">
                      {errors.email.message}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1.5 block  text-black">
                    Enter Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      className="h-11 w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 pr-11 text-base text-black outline-none placeholder:text-[#98A2B3] sm:text-xl"
                      {...register("password", {
                        required: "Password is required",
                        minLength: {
                          value: 8,
                          message: "Password must be at least 8 characters",
                        },
                        validate: {
                          hasLetter: (value) =>
                            /[A-Za-z]/.test(value) ||
                            "Password must contain at least one letter",
                          hasSpecialCharacter: (value) =>
                            /[^A-Za-z0-9]/.test(value) ||
                            "Password must contain at least one special character",
                        },
                      })}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((current) => !current)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-[#667085]"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password?.message ? (
                    <p className="mt-1.5 text-sm text-[#DC2626]">
                      {errors.password.message}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1.5 block  text-black">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm password"
                      className="h-11 w-full rounded-[5px] border border-[#00000014] bg-[#D9D9D961] px-4 pr-11 text-base text-black outline-none placeholder:text-[#98A2B3] sm:text-xl"
                      {...register("confirmPassword", {
                        required: "Confirm password is required",
                        validate: (value, formValues) =>
                          value === formValues.password || "Passwords do not match",
                      })}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                      onClick={() =>
                        setShowConfirmPassword((current) => !current)
                      }
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-[#667085]"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword?.message ? (
                    <p className="mt-1.5 text-sm text-[#DC2626]">
                      {errors.confirmPassword.message}
                    </p>
                  ) : null}
                </div>
              </div>
              {apiError ? (
                <p className="mt-4 text-sm font-medium text-[#DC2626]">
                  {apiError}
                </p>
              ) : null}
              </form>

              <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-5">
                <button
                  type="button"
                  className="h-10 rounded-lg bg-[#DBDBDB] px-6 text-base font-medium text-[#686868] sm:h-8 sm:text-lg"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSubmitting || isLoading}
                  className="h-10 rounded-lg bg-[#923D8C] px-5 text-base font-medium text-[#FAFCFE] disabled:cursor-not-allowed disabled:opacity-70 sm:h-8 sm:text-lg"
                  onClick={handleSubmit(onSubmit)}
                >
                  {isSubmitting || isLoading ? "Creating..." : "Create User"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isSuccessModalOpen ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden bg-black/45 px-3 py-4 sm:p-4"
          onClick={handleSuccessClose}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-[#00000014] bg-white p-5 text-center sm:p-8"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F7EC] text-[#198754]">
              <Check/>
            </div>
            <h4 className="text-2xl font-semibold text-[#101828]">Success</h4>
            <p className="mt-2 text-base text-[#667085]">
              User created successfully.
            </p>
            <button
              type="button"
              className="mt-6 h-10 rounded-lg bg-[#923D8C] px-6 text-base font-medium text-white"
              onClick={handleSuccessClose}
            >
              OK
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default CreateUserModal;
